import type { IndustryPage } from '../industries';

const lines = [
  { item: 'Seasonal plowing, Nov 15 to Mar 31: two-car driveway and apron, plowed at a 2 in trigger, unlimited pushes', amount: '$650' },
  { item: 'Hand shovel front walk and porch steps on every plow visit', amount: '$240' },
  { item: 'Pet-safe ice melt on walk and steps after each storm, material included', amount: '$180' },
  { item: 'Optional: roof rake over the front porch after storms of 8 in or more', amount: '$150' },
];

const faq = [
  {
    q: 'Should I sell snow removal per push or as a seasonal contract?',
    a: 'Both are common. A seasonal price gives you steady revenue and the customer a fixed cost whether the winter is mild or brutal; per-push pricing tracks the actual snowfall. Many operators offer both and let the customer choose. Whichever you use, write the trigger depth, the season dates and what counts as a visit.',
  },
  {
    q: 'What is a trigger depth?',
    a: 'The amount of snow that has to fall before you come out, often a couple of inches for driveways. Put it on the contract so a customer does not call at half an inch expecting a plow.',
  },
  {
    q: 'Can Scopie bill a seasonal contract in monthly installments automatically?',
    a: 'Not yet. Scopie has no recurring billing today. You can quote the season as one contract and create the invoices you agree on, such as one in November and one in January, and email each invoice link.',
  },
  {
    q: 'How do I offer roof raking or salting as extras?',
    a: 'Add them to the contract as optional lines. The customer ticks the extras they want when they open it, and the season total updates before they sign.',
  },
];

const page: IndustryPage = {
  slug: 'snow-removal',
  order: 17,
  group: 'Outdoor',
  path: '/for/snow-removal',
  primaryKeyword: 'snow removal software',
  secondaryKeywords: ['snow removal contract template', 'snow plowing contract', 'how to price snow removal'],
  title: 'Snow Removal Software for Seasonal Contracts | Scopie', // 53
  metaDescription:
    'Sign snow removal customers before the first storm: season dates, trigger depth, walks and salt on one contract, with roof raking as an optional extra.', // 151
  h1: 'Snow removal contracts signed before the first flake',
  answerFirst:
    'Scopie helps snow removal operators sign residential and small commercial customers before winter: plowing, shovelling and salting on one contract with the season dates and trigger depth written in. Roof raking or extra salting are optional lines the customer can tick. Email the contract in October, collect signatures, and plan routes by hand.',
  exampleQuote: { label: 'Sample seasonal snow contract', lines, total: '$1,220', depositNote: 'Season dates, trigger depth and payment timing are the operator\'s own clauses.' },
  blocks: [
    {
      type: 'callout',
      id: 'october',
      text: 'Snow removal is sold in October and November. By the first storm, the customers who planned ahead are signed, and the rest are calling whoever answers.',
    },
    {
      type: 'sample-quote',
      id: 'sample',
      h2: 'One driveway, one season',
      label: 'Sample seasonal contract: driveway, walk and salt',
      lines,
      total: '$1,220',
      totalLabel: 'Season total with roof raking',
      note: 'Example figures only. Roof raking is optional and the customer can leave it off.',
    },
    {
      type: 'list',
      id: 'contract',
      h2: 'Terms every snow contract needs',
      style: 'checklist',
      items: [
        { text: 'Season start and end dates, and what happens to snow outside them.' },
        { text: 'Trigger depth for plowing and for walks, which may differ.' },
        { text: 'Response window after snowfall stops, and priority order if you serve businesses first.' },
        { text: 'Who is responsible for marking edges, curbs and landscape beds with stakes.' },
        { text: 'Damage to lawns, pavers and mailboxes hidden under snow, and how claims are handled.' },
        { text: 'Ice: whether salting is included, and that it reduces but does not remove slip risk.' },
      ],
    },
    {
      type: 'text',
      id: 'pricing-models',
      h2: 'Seasonal, per push or per event',
      lead: 'Last winter it barely snowed and the seasonal customers felt cheated. The winter before, you lost money on every one of them.',
      paragraphs: [
        'Seasonal contracts smooth your income and your customers\' bills, and the risk of a heavy winter sits with you. Per-push pricing is fair in both directions but makes revenue unpredictable. A capped seasonal price, with per-push charges after a set number of visits, splits the difference.',
        'Write the model you are selling into the contract in plain words. Customers who understand what they bought do not argue in March.',
      ],
    },
    {
      type: 'list',
      id: 'mistakes',
      h2: 'Snow contract mistakes that cost operators',
      style: 'mistakes',
      items: [
        { title: 'Selling too late', text: 'Contracts signed after the first storm leave no time to plan crews and routes.' },
        { title: 'No trigger depth', text: 'Every call at a dusting turns into a dispute about whether a visit was owed.' },
        { title: 'Salt as an unlimited freebie', text: 'Ice melt is a real cost per storm. Price it or cap it.' },
        { title: 'Unmarked edges', text: 'Plowing up a lawn or a paver border is a spring repair bill. Stake before the season.' },
        { title: 'Payment timing left vague', text: 'Say when invoices go out: in full up front, split in two, or after each visit.' },
      ],
    },
    {
      type: 'text',
      id: 'storm-nights',
      h2: 'Storm nights',
      paragraphs: [
        'Signed customers stay in your client list with their contract, so on a storm night you can see who is seasonal and who is per push. Put crews and trucks on the schedule; plow drivers see their addresses, not what each customer pays. Scopie does not plan routes, so build the run order the way you already do. Invoices go out by email as links when you bill.',
      ],
    },
    { type: 'faq', id: 'faq', h2: 'Snow removal contract questions', items: faq },
  ],
  sections: [],
  faq,
  internalLinks: [
    { href: '/for', anchor: 'All industries', status: 'live' },
    { href: '/pricing', anchor: 'Scopie plans and pricing', status: 'live' },
    { href: '/resources/how-to-price-snow-removal', anchor: 'how to price snow removal', status: 'live' },
    { href: '/for/landscaping', anchor: 'Scopie for landscaping businesses', status: 'live' },
    { href: '/for/holiday-lighting', anchor: 'Scopie for holiday lighting installers', status: 'live' },
  ],
  cta: {
    heading: 'Sign the winter before it starts',
    text: 'Set up your seasonal prices, trigger depths and extras in Scopie, and email every contract in October.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
  claimsToVerify: [],
};

export default page;
